// design/033 — Manager tools → Learner Responses. Every reflection and
// question learners send on the courses / practices you own, in one place:
// one list (needs-reply first, program filter, search) + detail pane with the
// prompt, the thread and reply. Opening one marks it
// seen (the sidebar badge counts unseen ones).
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync, useRefreshOnFocus } from '../components/ui';
import RichText from '../components/RichText';
import RichTextEditor from '../components/RichTextEditor';
import '../styles/learn.css';

const errText = (e) => (e?.data?.message || e?.message || 'Something went wrong').replace(/^[A-Z_]+:\s*/, '');
const htmlEmpty = (h) => !h || !h.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
/** 📝 Reflection (green) / ❓ Question (blue) — shown on every response. */
function KindPill({ kind }) {
  const q = kind === 'QUESTION';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 700,
      color: q ? '#1e40af' : '#166534', background: q ? '#dbeafe' : '#dcfce7',
      border: `1px solid ${q ? '#93c5fd' : '#86efac'}`, borderRadius: 999, padding: '1px 9px',
    }}>
      {q ? '❓ Question' : '📝 Reflection'}
    </span>
  );
}

const changed = () => window.dispatchEvent(new Event('responses-changed'));

function ago(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}


export default function LearnerResponsesPage() {
  const [params, setParams] = useSearchParams();
  const program = params.get('program') || '';
  const query = params.get('q') || '';
  const [q, setQ] = useState(query);
  const [selected, setSelected] = useState(params.get('id') || null);

  const set = (patch) => {
    const p = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setParams(p, { replace: true });
  };

  const { loading, error, data, reload } = useAsync(
    () => api.responsesList({ status: 'all', program, q: query }), [program, query]);
  // 2026-10-15 — keep the list current while you're on this page: reload when
  // the sidebar count goes up, when the Learner Responses menu item is
  // clicked again (new location key), and when coming back to the tab.
  const { key: locationKey } = useLocation();
  const [firstKey] = useState(locationKey);
  useEffect(() => {
    const onNew = () => reload();
    window.addEventListener('responses-new', onNew);
    return () => window.removeEventListener('responses-new', onNew);
  }, [reload]);
  useEffect(() => { if (locationKey !== firstKey) reload(); }, [locationKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useRefreshOnFocus(reload);
  // One list: the ones waiting on you first (newest first), then the rest.
  // 2026-10-15: Needs reply first, longest-waiting first (a queue); then
  // everything else by when it last changed, newest first.
  const t = (iso) => (iso ? new Date(iso).getTime() : 0);
  const items = data ? [...data.items].sort((a, b) => {
    if (a.needsReply !== b.needsReply) return a.needsReply ? -1 : 1;
    if (a.needsReply) return t(a.waitingSince || a.lastActivityAt) - t(b.waitingSince || b.lastActivityAt);
    return t(b.lastActivityAt) - t(a.lastActivityAt);
  }) : [];
  const needCount = items.filter((r) => r.needsReply).length;

  return (
    <div className="page">
      <PageHead icon="💬" title="Learner responses"
        sub="Reflections and questions from your courses and practices"
        actions={<EmailSetting />} />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {data && (
          <span style={{ fontWeight: 600, color: needCount ? '#92400e' : 'var(--muted)', marginRight: 4 }}>
            {needCount ? `${needCount} need${needCount === 1 ? 's' : ''} a reply` : 'All caught up'}
          </span>
        )}
        <select className="input" style={{ width: 'auto', maxWidth: 260, padding: '4px 8px' }} value={program}
          onChange={(e) => set({ program: e.target.value })}>
          <option value="">All courses &amp; practices</option>
          {(data?.programs || []).map((p) => (
            <option key={`${p.kind}:${p.id}`} value={`${p.kind}:${p.id}`}>
              {p.kind === 'practice' ? '🧘' : '📚'} {p.title}
            </option>
          ))}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); set({ q: q.trim() }); }} style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <input className="input" style={{ width: 220 }} placeholder="Search learners or text…" value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn btn-sm">Search</button>
          {query && <button type="button" className="btn btn-sm" onClick={() => { setQ(''); set({ q: '' }); }}>Clear</button>}
        </form>
      </div>

      {loading && !data ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 380px) 1fr', gap: 14, alignItems: 'start' }}>
          <Section style={{ padding: 0, maxHeight: '75vh', overflowY: 'auto' }}>
            {items.length === 0 ? (
              <div style={{ padding: 18 }}>
                <Empty>No responses yet.</Empty>
              </div>
            ) : items.map((r) => (
              <button key={r.id} onClick={() => setSelected(r.id)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  background: selected === r.id ? 'var(--wpl-50, #eef2ff)' : r.needsReply ? '#fffbeb' : '#fff',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {r.unseen && <span title="New" style={{ width: 8, height: 8, borderRadius: 4, background: 'var(--wpl-600, #4f46e5)', flexShrink: 0 }} />}
                  <b style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: r.unseen ? 700 : 600 }}>{r.learnerName}</b>
                  <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{ago(r.lastActivityAt)}</span>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 5 }}>
                  <KindPill kind={r.kind} />
                  {r.needsReply && (
                    <span style={{
                      fontSize: '0.6875rem', fontWeight: 700, letterSpacing: 0.3,
                      color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 999, padding: '1px 8px',
                    }}>Needs reply</span>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>
                  {r.program?.kind === 'practice' ? '🧘' : '📚'} {r.program?.title}
                  {r.lessonTitle ? ` · ${r.lessonTitle}` : ''}
                </div>
                <div style={{ fontSize: '0.85rem', marginTop: 4, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.excerpt}
                </div>
              </button>
            ))}
          </Section>

          {/* Stays in view while the list (its own scroll box) is scrolled. */}
          <div style={{ position: 'sticky', top: 12, maxHeight: 'calc(100vh - 24px)', overflowY: 'auto' }}>
            {selected
              ? <ResponseDetail key={selected} id={selected} onChanged={() => { reload(); changed(); }} />
              : <Section><Empty>Choose a response on the left.</Empty></Section>}
          </div>
        </div>
      )}
    </div>
  );
}

function ResponseDetail({ id, onChanged }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, d: null });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.responseDetail(id)
      .then((d) => { if (alive) { setState({ loading: false, error: null, d }); onChanged(); } })
      .catch((error) => { if (alive) setState({ loading: false, error, d: null }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (state.loading) return <Section><Spinner /></Section>;
  if (state.error) return <Section><ErrorNote error={state.error} /></Section>;
  const { d } = state;
  const r = d.row;

  const act = async (fn) => {
    setBusy(true);
    try { const nd = await fn(); setState({ loading: false, error: null, d: nd }); onChanged(); }
    catch (e) { alert(errText(e)); }
    setBusy(false);
  };
  const reply = () => {
    if (htmlEmpty(text)) return;
    act(async () => { const nd = await api.responseReply(id, text); setText(''); return nd; });
  };

  return (
    <Section>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{r.learnerName}</span>
            <KindPill kind={r.kind} />
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 2 }}>
            {r.learnerUsername && r.learnerUsername !== r.learnerEmail ? `@${r.learnerUsername}` : r.learnerEmail} ·{' '}
            {r.program?.kind === 'practice' ? '🧘' : '📚'} {r.program?.title}{r.lessonTitle ? ` — ${r.lessonTitle}` : ''}
          </div>
        </div>
        <button className="btn btn-sm" onClick={() => navigate(d.openPath)}>
          Open {r.program?.kind === 'practice' ? 'practice' : 'lesson'} ↗
        </button>
      </div>

      {d.prompt && (
        <div className="block-reflection" style={{ marginBottom: 12 }}>
          <div className="rlabel">📝 Prompt</div>
          <div className="rtext"><RichText html={d.prompt} /></div>
        </div>
      )}

      <div className="submission-card">
        <div className="reply-who">{r.learnerName} · {new Date(r.createdAt).toLocaleString()}</div>
        <RichText html={d.body} />
      </div>

      {d.thread.length > 0 && (
        <div className="reply-thread">
          {d.thread.map((m) => (
            <div key={m.id} className={`reply-bubble${m.byInstructor ? ' from-instructor' : ''}`}>
              <div className="reply-who">{m.byInstructor ? `🎓 ${m.authorName}` : m.authorName} · {new Date(m.createdAt).toLocaleString()}</div>
              <RichText html={m.body} />
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <RichTextEditor value={text} onChange={setText} minHeight={80} placeholder={`Reply to ${r.learnerName}…`} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={busy || htmlEmpty(text)} onClick={reply}>
            {busy ? 'Sending…' : 'Send reply'}
          </button>
          <span style={{ fontSize: '0.8rem', color: 'var(--muted)', marginLeft: 'auto' }}>
            {r.learnerName.split(' ')[0]} gets an email when you reply.
          </span>
        </div>
      </div>
    </Section>
  );
}

/** "Email me: Daily summary / Immediately / Off" — instructor setting. */
function EmailSetting() {
  const { data, reload } = useAsync(() => api.responseSettings().catch(() => ({ responseEmail: 'DIGEST' })), []);
  const value = data?.responseEmail || 'DIGEST';
  const change = async (v) => {
    try { await api.setResponseSettings(v); reload(); }
    catch (e) { alert(errText(e)); }
  };
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--muted)' }}>
      Email me:
      <select className="input" style={{ width: 'auto', padding: '4px 8px' }} value={value} onChange={(e) => change(e.target.value)}>
        <option value="DIGEST">A daily summary</option>
        <option value="IMMEDIATE">Every new response</option>
        <option value="NONE">Never</option>
      </select>
    </label>
  );
}
